import { useCallback, useState } from "react";
import { addToast } from "@heroui/react";
import { DECLARATION_VERSION, TRequestDeclaration } from "@shared/entities";
import { firebaseApi } from "src/firebase-api";

/**
 * The patient declaration a client must accept before submitting a request.
 * `confirm` refuses (and says why) until it is accepted, then has the server
 * record the acceptance and returns what to save on the request.
 */
export function useSubmissionDeclaration() {
	const [accepted, setAccepted] = useState(false);
	const [renewalContactConsent, setRenewalContactConsent] = useState(false);
	const [showError, setShowError] = useState(false);
	// Bumped on every refused attempt, so the checkbox is brought into view
	// each time — not only the first.
	const [errorAttempt, setErrorAttempt] = useState(0);

	const onAcceptedChange = useCallback((value: boolean) => {
		setAccepted(value);
		if (value) setShowError(false);
	}, []);

	/**
	 * Marks the declaration red when it is not accepted yet; true if it is.
	 * `scroll: false` when another error on the page is the one to show first.
	 */
	const markIfMissing = useCallback(
		({ scroll = true } = {}) => {
			if (accepted) return true;
			setShowError(true);
			if (scroll) setErrorAttempt((n) => n + 1);
			return false;
		},
		[accepted],
	);

	const confirm = useCallback(
		async (requestId: string): Promise<TRequestDeclaration | null> => {
			if (!markIfMissing()) {
				addToast({
					title: "יש לאשר את ההצהרה",
					description: "כדי לשלוח את הבקשה, סמן/י ״קראתי ואני מאשר/ת את כל האמור לעיל״.",
					color: "danger",
				});
				return null;
			}
			try {
				return await firebaseApi.api.recordSubmissionDeclaration({
					requestId,
					version: DECLARATION_VERSION,
					renewalContactConsent,
				});
			} catch (error) {
				addToast({
					title: "לא הצלחנו לשמור את האישור",
					description: (error as Error).message || "נסה/י שוב בעוד רגע.",
					color: "danger",
				});
				return null;
			}
		},
		[markIfMissing, renewalContactConsent],
	);

	return {
		accepted,
		onAcceptedChange,
		renewalContactConsent,
		setRenewalContactConsent,
		showError,
		errorAttempt,
		markIfMissing,
		confirm,
	};
}
